import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Collapse, Descriptions, Empty, List, Space, Table, Tabs, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import {
  AcceptanceDataSnapshots,
  AcceptanceFormulaEvidence,
  AcceptanceManifest,
  AcceptanceProjectedControl,
  AcceptanceRuntimeFieldCoverage,
} from './types';
import { useAcceptanceMode } from './AcceptanceModeContext';
import { runObserverChecks } from './observerChecks';
import { buildSnapshotDiff } from './snapshotDiff';
import { AcceptanceChangeRequestButton } from './AcceptanceChangeRequestButton';
import { AcceptanceChangeRequestList } from './AcceptanceChangeRequestList';
import { acceptanceProjectionApi } from '../../services/acceptanceProjectionApi';
import { AcceptanceRunControls } from './AcceptanceRunControls';
import { AcceptanceInlineFieldHints } from './AcceptanceInlineFieldHints';
import { mergeAcceptanceRuntimeFieldCoverage } from './runtimeFieldCoverage';

interface Props {
  manifest: AcceptanceManifest;
  documentKey?: string;
  snapshots: AcceptanceDataSnapshots;
  latestRecords: unknown[];
  history: unknown[];
  auditEntries?: unknown[];
  loading?: boolean;
  onRefresh?: () => void;
  observationErrors?: string[];
  formulaEvidence?: AcceptanceFormulaEvidence[];
  runtimeFieldCoverage?: AcceptanceRuntimeFieldCoverage;
}

const JsonBlock: React.FC<{ value: unknown }> = ({ value }) => (
  value !== undefined && value !== null
    ? <pre style={{ maxHeight: 360, overflow: 'auto', background: '#111827', color: '#d1fae5', padding: 12, borderRadius: 6, fontSize: 12 }}>{JSON.stringify(value, null, 2)}</pre>
    : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="目前沒有資料" />
);

const ControlList: React.FC<{ controls: AcceptanceProjectedControl[] }> = ({ controls }) => (
  <List
    bordered
    size="small"
    locale={{ emptyText: 'Contract 沒有這一類可機讀規則' }}
    dataSource={controls}
    renderItem={item => <List.Item>
      <div>
        <strong>{item.plainLanguage.summary || item.label}</strong>
        {item.requiredPermission && <Tag color="purple">{item.requiredPermission}</Tag>}
        {item.api?.method && <Tag color="blue">{item.api.method} {item.api.path}</Tag>}
        {item.from !== undefined && <Tag>{String(item.from)} → {String(item.to)}</Tag>}
        {item.plainLanguage.businessReason && <div><strong>目的：</strong>{item.plainLanguage.businessReason}</div>}
        {item.plainLanguage.businessImpact && <div><strong>影響：</strong>{item.plainLanguage.businessImpact}</div>}
        <Collapse ghost size="small" items={[{
          key: item.controlId,
          label: '查看技術依據',
          children: <>
            <div><Tag>{item.controlId}</Tag><Tag color={item.evidenceStatus === 'confirmed' ? 'green' : item.evidenceStatus === 'manual_review' ? 'orange' : 'gold'}>{item.sourceStatus}</Tag></div>
            <div><code>{item.technicalDescription}</code></div>
            <Typography.Text type="secondary">來源：{item.sourceRefs.length ? item.sourceRefs.join('、') : '缺少 source_refs，待補約'}</Typography.Text>
          </>,
        }]} />
      </div>
    </List.Item>}
  />
);

const GapList: React.FC<{ gaps: AcceptanceManifest['disclosedGaps']; technical?: boolean }> = ({ gaps, technical = false }) => (
  <List
    size="small"
    dataSource={gaps}
    locale={{ emptyText: '沒有這一類事項' }}
    renderItem={gap => <List.Item>
      <div style={{ width: '100%' }}>
        <strong>{gap.title}</strong>
        <Tag style={{ marginLeft: 8 }} color={gap.status === 'resolved' ? 'green' : 'orange'}>{gap.status === 'resolved' ? '已解決' : '待確認'}</Tag>
        {gap.currentBehavior && <div><strong>目前做法：</strong>{gap.currentBehavior}</div>}
        {gap.uncertainty && <div><strong>還不確定：</strong>{gap.uncertainty}</div>}
        {gap.businessImpact && <div><strong>可能影響：</strong>{gap.businessImpact}</div>}
        {gap.choices.length > 0 && <div style={{ marginTop: 6 }}>
          <strong>可選方向：</strong>
          <Space size={[4, 4]} wrap>{gap.choices.map(choice => <Tag key={choice.value} color="blue">{choice.label}</Tag>)}</Space>
        </div>}
        {technical && <Collapse ghost size="small" items={[{
          key: gap.gapId,
          label: '查看原始技術問題與證據',
          children: <>
            <div><Tag>{gap.gapId}</Tag><Tag>{gap.category}</Tag><Tag>{gap.audience}</Tag></div>
            <Typography.Paragraph><code>{gap.technicalDescription}</code></Typography.Paragraph>
            <Typography.Text type="secondary">來源：{gap.sourceRefs.length ? gap.sourceRefs.join('、') : '缺 source_refs'}</Typography.Text>
          </>,
        }]} />}
      </div>
    </List.Item>}
  />
);

const FormulaList: React.FC<{
  manifest: AcceptanceManifest;
  evidence: AcceptanceFormulaEvidence[];
}> = ({ manifest, evidence }) => (
  <Collapse
    items={manifest.formulas.map(formula => {
      const runtime = evidence.find(item => item.formulaId === formula.formulaId);
      const statusColor = runtime?.status === 'pass' ? 'green' : runtime?.status === 'fail' ? 'red' : 'orange';
      return {
        key: formula.formulaId,
        label: <>
          <strong>{formula.label}</strong>
          <Tag style={{ marginLeft: 8 }}>{formula.formulaId}</Tag>
          <Tag color="blue">{formula.authority}</Tag>
          <Tag color={statusColor}>{runtime?.status || 'not_observed'}</Tag>
        </>,
        children: <>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="業務目的">{formula.businessPurpose}</Descriptions.Item>
            <Descriptions.Item label="觸發動作">{formula.triggerAction}</Descriptions.Item>
            <Descriptions.Item label="公式（安全描述）"><code>{formula.expressionDisplay}</code></Descriptions.Item>
            <Descriptions.Item label="精度／捨入">{formula.precision.scale} 位／{formula.precision.roundingMode}</Descriptions.Item>
            <Descriptions.Item label="輸入">{formula.inputs.map(item => `${item.fieldId} (${item.role})`).join('、')}</Descriptions.Item>
            <Descriptions.Item label="輸出">{formula.outputs.map(item => item.fieldId).join('、')}</Descriptions.Item>
            <Descriptions.Item label="條件">{formula.conditions.join('；') || '無'}</Descriptions.Item>
            <Descriptions.Item label="DB 對應">{formula.dbMappings.map(item => `${item.table}.${item.column} (${item.role})`).join('、')}</Descriptions.Item>
            <Descriptions.Item label="來源">{formula.sourceRefs.join('、')}</Descriptions.Item>
            <Descriptions.Item label="驗證器">{formula.verifierIds.join('、')}</Descriptions.Item>
            {formula.knownDivergence && <Descriptions.Item label="已知邊界／差異">{formula.knownDivergence}</Descriptions.Item>}
          </Descriptions>
          <Typography.Title level={5} style={{ marginTop: 12 }}>本次執行證據</Typography.Title>
          {runtime
            ? <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="結論"><Tag color={statusColor}>{runtime.status}</Tag> {runtime.detail}</Descriptions.Item>
              <Descriptions.Item label="輸入"><JsonBlock value={runtime.inputs} /></Descriptions.Item>
              <Descriptions.Item label="中間值"><JsonBlock value={runtime.intermediateValues} /></Descriptions.Item>
              <Descriptions.Item label="API 權威結果"><JsonBlock value={runtime.apiResult} /></Descriptions.Item>
              <Descriptions.Item label="DB 結果"><JsonBlock value={runtime.databaseResult} /></Descriptions.Item>
            </Descriptions>
            : <Alert type="warning" showIcon message="尚未觀測本次公式執行；這不是 PASS。" />}
          <div style={{ marginTop: 12 }}>
            <AcceptanceChangeRequestButton
              manifest={manifest}
              contextType="formula"
              controlId={formula.formulaId}
              inputSummary={runtime?.inputs}
              evidenceContext={runtime}
              label="提出公式 Contract 變更"
            />
          </div>
        </>,
      };
    })}
  />
);

const displayValue = (value: unknown) => value === undefined ? '—' : typeof value === 'string' ? value : JSON.stringify(value);

export const AcceptanceDataPanel: React.FC<Props> = ({
  manifest, documentKey, snapshots, latestRecords, history, auditEntries = [],
  loading, onRefresh, observationErrors = [], formulaEvidence = [],
  runtimeFieldCoverage: suppliedRuntimeFieldCoverage,
}) => {
  const { isEnabled } = useAcceptanceMode();
  const enabled = isEnabled(manifest.formId);
  const [internalRuntimeFieldCoverage, setInternalRuntimeFieldCoverage] = useState<
    AcceptanceRuntimeFieldCoverage | undefined
  >();
  const internalCoverageAnchorRef = useRef<HTMLSpanElement>(null);
  const ownsRuntimeFieldCoverage = suppliedRuntimeFieldCoverage === undefined;
  const updateInternalRuntimeFieldCoverage = useCallback((next: AcceptanceRuntimeFieldCoverage) => {
    setInternalRuntimeFieldCoverage(current => (
      mergeAcceptanceRuntimeFieldCoverage(current, next, manifest.fields)
    ));
  }, [manifest.fields]);
  useEffect(() => {
    if (ownsRuntimeFieldCoverage) setInternalRuntimeFieldCoverage(undefined);
  }, [enabled, manifest.contractHash, manifest.formId, ownsRuntimeFieldCoverage]);
  const runtimeFieldCoverage = suppliedRuntimeFieldCoverage || internalRuntimeFieldCoverage;
  const acknowledgementKey = `erp.acceptance-gap-ack.${manifest.formId}.${manifest.contractHash}`;
  const unresolvedGaps = useMemo(
    () => manifest.disclosedGaps.filter(gap => gap.status !== 'resolved'),
    [manifest.disclosedGaps],
  );
  const userDecisionGaps = useMemo(
    () => unresolvedGaps.filter(gap => gap.audience !== 'developer'),
    [unresolvedGaps],
  );
  const developerGaps = useMemo(
    () => unresolvedGaps.filter(gap => gap.audience === 'developer'),
    [unresolvedGaps],
  );
  const resolvedGaps = useMemo(
    () => manifest.disclosedGaps.filter(gap => gap.status === 'resolved'),
    [manifest.disclosedGaps],
  );
  const [gapsAcknowledged, setGapsAcknowledged] = useState(
    () => userDecisionGaps.length === 0 || localStorage.getItem(acknowledgementKey) === 'true',
  );
  const [serverProjectionHash, setServerProjectionHash] = useState<string>();
  const [projectionError, setProjectionError] = useState('');
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    acceptanceProjectionApi.get(manifest.formId).then(projection => {
      if (cancelled) return;
      setServerProjectionHash(projection.contractHash);
      setProjectionError(projection.contractHash === manifest.contractHash
        ? ''
        : `前端 Contract hash ${manifest.contractHash.slice(0, 12)} 與後端 ${projection.contractHash.slice(0, 12)} 不一致`);
    }).catch(() => {
      if (!cancelled) setProjectionError('後端 Contract projection 讀取失敗');
    });
    return () => { cancelled = true; };
  }, [enabled, manifest.contractHash, manifest.formId]);
  const effectiveObservationErrors = useMemo(
    () => [...observationErrors, ...(projectionError ? [projectionError] : [])],
    [observationErrors, projectionError],
  );
  const observerChecks = useMemo(() => runObserverChecks(manifest, {
    documentKey,
    latestRecords,
    auditEntries,
    errors: effectiveObservationErrors,
    snapshots,
  }), [auditEntries, documentKey, effectiveObservationErrors, latestRecords, manifest, snapshots]);
  const uiFieldCoverageReady = runtimeFieldCoverage !== undefined;
  const uiFieldCoveragePassed = uiFieldCoverageReady
    && runtimeFieldCoverage.unmodeledFields.length === 0
    && runtimeFieldCoverage.unmountedProjectedFieldIds.length === 0
    && runtimeFieldCoverage.duplicateProjectedFieldIds.length === 0
    && runtimeFieldCoverage.candidateCount === runtimeFieldCoverage.bindings.length
    && runtimeFieldCoverage.modeledFields.length === manifest.fields.length;
  const observerPassed = observerChecks.every(check => check.passed) && uiFieldCoveragePassed;
  const snapshotDiff = useMemo(
    () => buildSnapshotDiff(snapshots.screenDraft, snapshots.lastApiResponse, snapshots.databaseRecord),
    [snapshots.databaseRecord, snapshots.lastApiResponse, snapshots.screenDraft],
  );
  const mappingFields = useMemo(() => {
    const fields = new Map(manifest.fields.map(field => [field.fieldId, field]));
    runtimeFieldCoverage?.unmodeledFields.forEach(field => fields.set(field.fieldId, field));
    return Array.from(fields.values());
  }, [manifest.fields, runtimeFieldCoverage]);

  if (!enabled) return null;

  const mappingColumns = [
    { title: '畫面欄位', dataIndex: 'label', key: 'label' },
    { title: '穩定 ID', dataIndex: 'fieldId', key: 'fieldId' },
    { title: 'Contract control', key: 'controls', render: (_: unknown, row: typeof manifest.fields[number]) => row.controlIds.length ? row.controlIds.join(', ') : <Tag color="orange">未建模</Tag> },
    { title: '資料庫', key: 'db', render: (_: unknown, row: typeof manifest.fields[number]) => `${row.table}.${row.column}` },
    { title: '規則', key: 'rules', render: (_: unknown, row: typeof manifest.fields[number]) => <>{row.required && <Tag color="red">必填</Tag>}{row.readOnly && <Tag>唯讀</Tag>}{row.validationGuidance.map(rule => <div key={rule.controlId}>{rule.summary}</div>)}</> },
    { title: '證據狀態', dataIndex: 'evidenceStatus', key: 'evidenceStatus' },
  ];
  const controlTabs = [
    { key: 'formulas', label: `計算公式 (${manifest.formulas.length})`, children: manifest.formulas.length
      ? <FormulaList manifest={manifest} evidence={formulaEvidence} />
      : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Contract 尚未建立 machine-readable formulas[]" /> },
    { key: 'actions', label: '動作／API', children: <ControlList controls={manifest.actions} /> },
    { key: 'validation', label: '驗證規則', children: <ControlList controls={manifest.validations} /> },
    { key: 'workflow', label: '權限／流程', children: <><Typography.Title level={5}>權限</Typography.Title><ControlList controls={manifest.permissions} /><Typography.Title level={5}>工作流</Typography.Title><ControlList controls={manifest.workflows} /></> },
    { key: 'effects', label: 'DB／副作用', children: <><Typography.Title level={5}>讀取</Typography.Title><ControlList controls={manifest.dbReads} /><Typography.Title level={5}>寫入</Typography.Title><ControlList controls={manifest.dbWrites} /><Typography.Title level={5}>副作用及禁止寫入</Typography.Title><ControlList controls={manifest.sideEffects} /></> },
  ];

  const panel = (
    <Collapse style={{ marginTop: 16 }} items={[{
      key: 'acceptance-observer',
      label: <><strong>驗收資料與 Contract 觀測器</strong> <Tag color="cyan">唯讀</Tag> <Typography.Text type="secondary">文件：{documentKey || '新增中'}</Typography.Text></>,
      children: (
        <>
          <AcceptanceRunControls
            manifest={manifest}
            documentKey={documentKey}
            canPass={observerPassed && gapsAcknowledged}
            evidenceSummary={{
              observerChecks,
              snapshotDiff,
              formulaEvidence,
              contractHash: manifest.contractHash,
              runtimeFieldCoverage,
            }}
          />
          <Tag color={manifest.riskLevel === 'high' ? 'red' : manifest.riskLevel === 'medium' ? 'orange' : 'green'} style={{ marginBottom: 8 }}>
            {manifest.riskLevel === 'high' ? '高風險完整驗收' : manifest.riskLevel === 'medium' ? '中風險標準驗收' : '低風險輕量驗收'}
          </Tag>
          <Alert
            type={
              manifest.coverage.unmodeledUiFields.length
              || manifest.coverage.controlsMissingSourceRefs.length
              || runtimeFieldCoverage?.unmodeledFields.length
              || runtimeFieldCoverage?.unmountedProjectedFieldIds.length
              || runtimeFieldCoverage?.duplicateProjectedFieldIds.length
                ? 'warning'
                : 'success'
            }
            showIcon
            message={`Contract 投影：${manifest.projectionStatus}｜前端 ${manifest.contractHash.slice(0, 12)}｜後端 ${serverProjectionHash?.slice(0, 12) || '讀取中'}`}
            description={[
              `Contract 投影欄位 ${manifest.coverage.projectedFields}`,
              `目前畫面辨識 ${runtimeFieldCoverage?.candidateCount ?? '讀取中'} 個欄位`,
              `畫面未建模 ${runtimeFieldCoverage?.unmodeledFields.length ?? '讀取中'}`,
              `投影未掛載 ${runtimeFieldCoverage?.unmountedProjectedFieldIds.length ?? '讀取中'}`,
              `重複 Contract fieldId ${runtimeFieldCoverage?.duplicateProjectedFieldIds.length ?? '讀取中'}`,
              `一對一綁定 ${runtimeFieldCoverage?.bindings.length ?? '讀取中'}/${runtimeFieldCoverage?.candidateCount ?? '讀取中'}`,
              `缺 source_refs controls ${manifest.coverage.controlsMissingSourceRefs.length}`,
            ].join('；')}
            style={{ marginBottom: 12 }}
          />
          {unresolvedGaps.length > 0 && <Alert
            type="warning"
            showIcon
            message={`需要驗收者確認 ${userDecisionGaps.length} 項；另有開發者待辦 ${developerGaps.length} 項`}
            description={<>
              <GapList gaps={userDecisionGaps} technical />
              {userDecisionGaps.length > 0 && <Checkbox checked={gapsAcknowledged} onChange={event => {
                  const checked = event.target.checked;
                  setGapsAcknowledged(checked);
                  localStorage.setItem(acknowledgementKey, String(checked));
                }}>我已閱讀並理解上述待確認事項（這不是最終簽收）</Checkbox>}
              {(developerGaps.length > 0 || resolvedGaps.length > 0) && <Collapse
                style={{ marginTop: 10 }}
                items={[
                  ...(developerGaps.length > 0 ? [{
                    key: 'developer-gaps',
                    label: `開發者／稽核待辦（${developerGaps.length}）`,
                    children: <GapList gaps={developerGaps} technical />,
                  }] : []),
                  ...(resolvedGaps.length > 0 ? [{
                    key: 'resolved-gaps',
                    label: `已解決事項（${resolvedGaps.length}，不計入未確認）`,
                    children: <GapList gaps={resolvedGaps} technical />,
                  }] : []),
                ]}
              />}
            </>}
            style={{ marginBottom: 12 }}
          />}
          <Alert
            type={observerPassed ? 'success' : 'error'}
            showIcon
            message={observerPassed ? '觀測器自我檢查通過' : '觀測器無法證明資料正確，禁止視為驗收證據'}
            description={<>
              <div>{uiFieldCoveragePassed ? '✓' : '✕'} 畫面欄位完整性：
                {!uiFieldCoverageReady
                  ? '尚未掃描'
                  : `已辨識 ${runtimeFieldCoverage.candidateCount} 個；一對一綁定 ${runtimeFieldCoverage.bindings.length} 個；未建模 ${runtimeFieldCoverage.unmodeledFields.length} 個；投影未掛載 ${runtimeFieldCoverage.unmountedProjectedFieldIds.length} 個；重複 fieldId ${runtimeFieldCoverage.duplicateProjectedFieldIds.length} 個`}
              </div>
              {observerChecks.map(check => <div key={check.checkId}>{check.passed ? '✓' : '✕'} {check.label}：{check.detail}</div>)}
            </>}
            style={{ marginBottom: 12 }}
          />
          <Tabs items={[
            { key: 'screen', label: '目前畫面內容', children: <><Alert type="info" showIcon message="瀏覽器現在準備送出的內容，可能尚未儲存。" style={{ marginBottom: 8 }} /><JsonBlock value={snapshots.screenDraft} /></> },
            { key: 'api', label: '最近一次業務 API 回應', children: <><Alert type="info" showIcon message="開啟或儲存單據時，原本業務 API 最近一次成功回傳的資料。" style={{ marginBottom: 8 }} /><JsonBlock value={snapshots.lastApiResponse} /></> },
            { key: 'database', label: 'DB 即時內容', children: <><Alert type="info" showIcon message="按重新讀取後，由原本單據查詢 API 再次從資料庫取得；新增草稿在儲存前沒有 DB 單據。" style={{ marginBottom: 8 }} /><JsonBlock value={snapshots.databaseRecord} /></> },
            { key: 'diff', label: '畫面／API／DB 差異', children: <Table
              rowKey="path" size="small" pagination={{ pageSize: 20 }} dataSource={snapshotDiff}
              columns={[
                { title: '欄位路徑', dataIndex: 'path', key: 'path' },
                { title: '畫面', dataIndex: 'screen', key: 'screen', render: displayValue },
                { title: 'API', dataIndex: 'api', key: 'api', render: displayValue },
                { title: 'DB', dataIndex: 'database', key: 'database', render: displayValue },
                { title: '結果', dataIndex: 'status', key: 'status', render: (status: string) => <Tag color={status === 'same' ? 'green' : status === 'screen_changed' ? 'blue' : status === 'api_db_mismatch' ? 'red' : 'default'}>{status}</Tag> },
              ]} scroll={{ x: 1000 }}
            /> },
            { key: 'latest', label: '最新三筆', children: <JsonBlock value={latestRecords} /> },
            { key: 'history', label: '審核／Audit 歷程', children: <JsonBlock value={history} /> },
            { key: 'audit', label: 'Audit 證據', children: <JsonBlock value={auditEntries} /> },
            ...controlTabs,
            { key: 'change-requests', label: 'Contract 變更提案', children: <AcceptanceChangeRequestList manifest={manifest} /> },
            { key: 'mapping', label: '欄位與 DB 對照', children: <Table rowKey="fieldId" size="small" pagination={{ pageSize: 20 }} dataSource={mappingFields} columns={mappingColumns} scroll={{ x: 1100 }} /> },
            { key: 'tables', label: '讀寫資料表', children: <Descriptions bordered size="small" column={1} items={manifest.tables.map(table => ({ key: table.table, label: table.table, children: <>{table.role} <Tag color={table.access === 'read' ? 'blue' : table.access === 'audit' ? 'purple' : 'orange'}>{table.access}</Tag> Key: {table.keyFields.join(', ')}</> }))} /> },
            { key: 'history-cases', label: '真實／歷史案例', children: <List bordered dataSource={manifest.historicalCases} renderItem={item => <List.Item><div><strong>{item.title}</strong> <Tag>{item.kind}</Tag><Tag color={item.sourceStatus === 'confirmed' ? 'green' : 'gold'}>{item.sourceStatus}</Tag><div><Typography.Text type="secondary">{item.caseId}｜{item.sourceRefs.join('、')}</Typography.Text></div></div></List.Item>} /> },
          ]} />
        </>
      ),
      extra: <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={event => { event.stopPropagation(); onRefresh?.(); }}>重新讀取</Button>,
    }]} />
  );
  if (!ownsRuntimeFieldCoverage) return panel;

  const serializedRuntimeFieldCoverage = runtimeFieldCoverage
    ? JSON.stringify(runtimeFieldCoverage)
    : '';
  const serializedMountTriggers = JSON.stringify(
    manifest.fields
      .filter(field => field.conditionallyMounted && field.mountTrigger)
      .map(field => ({
        fieldId: field.fieldId,
        trigger: field.mountTrigger,
      })),
  );
  return (
    <>
      <span
        ref={internalCoverageAnchorRef}
        data-acceptance-inline-hints-anchor={manifest.formId}
        style={{ display: 'none' }}
      />
      <AcceptanceInlineFieldHints
        manifest={manifest}
        documentKey={documentKey}
        anchorRef={internalCoverageAnchorRef}
        onCoverageChange={updateInternalRuntimeFieldCoverage}
      />
      <div
        data-acceptance-surface={manifest.formId}
        data-acceptance-runtime-field-coverage={serializedRuntimeFieldCoverage}
        data-acceptance-mount-triggers={serializedMountTriggers}
      >
        {panel}
      </div>
    </>
  );
};
